import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api } from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { FormError } from '@/components/ui/form-error';
import { PageLoading, LoadingSpinner } from '@/components/ui/loading';
import { SearchableSelect, type SearchableSelectOption } from '@/components/ui/searchable-select';
import { ProjectAccessField, type ProjectAccessOption } from '@/components/projects/ProjectAccessField';
import { cn } from '@/lib/utils';
import { normalizeRole, ROLES } from '@/lib/permissions';
import { nameRule, emailRule, newPasswordRule, optionalPhoneRule, requiredText, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField, handleApiSubmitError } from '@/lib/form-validation';
import type { UserRole } from '@/lib/types';

const uniqueIdRule = requiredText('Unique ID', 50, 'Unique ID is required.')
  .regex(/^[a-zA-Z0-9._\-]+$/, 'Unique ID can only contain letters, numbers, dots, underscores, and hyphens.');

const schema = z.object({
  name: nameRule('Name', 200, 2),
  uniqueId: uniqueIdRule,
  email: z.union([z.literal(''), z.string().email('Enter a valid email address.')]),
  password: z.union([z.literal(''), newPasswordRule]),
  phone: optionalPhoneRule,
  roleId: z.string().min(1, 'Please select a role for this user.'),
});
type FormData = z.infer<typeof schema>;

/** Derive unique module names from a role's permissions, preserving group order. */
function deriveAccessModules(permissions: { group: string }[]): string[] {
  const seen = new Set<string>();
  const modules: string[] = [];
  for (const p of permissions) {
    if (!seen.has(p.group)) {
      seen.add(p.group);
      modules.push(p.group);
    }
  }
  return modules;
}

const PROJECTS_REQUIRED_MESSAGE = 'Please assign at least one project to this user.';

export default function UserFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const isEdit = !!id;
  const [viewAllPerms, setViewAllPerms] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [projectValidationError, setProjectValidationError] = useState('');

  const { register, handleSubmit, setValue, watch, reset, setError, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { roleId: '' },
  });
  const selectedRoleId = watch('roleId') || '';

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  const onSubmit = (data: FormData) => {
    // Project-based roles must have at least one project assigned at creation.
    if (!isEdit && !fullAccessRole && selectedProjectIds.length === 0) {
      setProjectValidationError(PROJECTS_REQUIRED_MESSAGE);
      addToast(PROJECTS_REQUIRED_MESSAGE, 'error');
      return;
    }
    setProjectValidationError('');
    const payload: any = { ...data };
    if (!fullAccessRole) {
      if (isEdit) {
        // Only send the assignment list once the existing assignments have
        // loaded, so a failed load can never wipe them accidentally.
        if (assignedProjectsQuery.isSuccess) payload.projectIds = selectedProjectIds;
      } else {
        payload.projectIds = selectedProjectIds;
      }
    }
    mutation.mutate(payload);
  };
  const onSubmitForm = handleSubmit(onSubmit, onInvalid);

  const { data: roles, isLoading: loadingRoles, isError: rolesError, refetch: refetchRoles } = useQuery({
    queryKey: ['assignable-roles'],
    queryFn: async () => { const r = await api.get<{ data: UserRole[] }>('/users/roles/assignable'); return r.data ?? []; },
  });
  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['user', id],
    queryFn: async () => { const r = await api.get<{ data: any }>(`/users/${id}`); return r.data; },
    enabled: isEdit,
  });

  // Current assignments (Edit User flow seeds the selection so the saved
  // projectIds match the existing assignments unless the admin changes them).
  const assignedProjectsQuery = useQuery({
    queryKey: ['user-projects', id],
    enabled: isEdit,
    queryFn: async () => {
      const r = await api.get<{ success: boolean; data: { id: string; name: string; status?: string; client_name?: string }[] }>(`/users/${id}/projects`);
      return r.data ?? [];
    },
  });

  // Projects available to assign (project-based roles only).
  const activeProjectsQuery = useQuery({
    queryKey: ['form-active-projects'],
    enabled: isEdit ? true : !!selectedRoleId,
    queryFn: async () => {
      const r = await api.get<{ success: boolean; data: { id: string; name: string; client_name?: string; status?: string }[] }>('/projects?availability=active&limit=1000');
      return r.data ?? [];
    },
  });
  const projectOptions: ProjectAccessOption[] = useMemo(() => {
    const map = new Map<string, ProjectAccessOption>();
    for (const p of activeProjectsQuery.data ?? []) {
      map.set(p.id, { value: p.id, label: p.name, sublabel: p.client_name || undefined });
    }
    // Merge in assignments that are no longer active so they stay visible.
    for (const a of assignedProjectsQuery.data ?? []) {
      if (!map.has(a.id)) {
        map.set(a.id, {
          value: a.id,
          label: a.status === 'active' ? a.name : `${a.name} (${a.status})`,
          sublabel: a.client_name || undefined,
        });
      }
    }
    return [...map.values()];
  }, [activeProjectsQuery.data, assignedProjectsQuery.data]);

  // Defensive filter: never render SUPER_ADMIN even if stale response includes it.
  const assignableRoles = useMemo(() => (roles ?? []).filter((r) => r.name !== 'SUPER_ADMIN'), [roles]);

  // On Edit User, if the existing role is inactive, it won't appear in assignableRoles.
  // We inject it as a disabled option so the summary can display it with a warning.
  const existingUserRole = useMemo(() => {
    if (!existing || !assignableRoles.length) return null;
    const userRoleId = existing.roles?.[0]?.role_id;
    if (!userRoleId) return null;
    const found = assignableRoles.find((r) => r.id === userRoleId);
    if (found) return null; // already in the list
    // Role is inactive or otherwise missing from assignable list
    return { id: userRoleId, name: existing.roles?.[0]?.name || 'Unknown', description: null, isActive: false, permissionCount: 0, permissions: [] } as UserRole;
  }, [existing, assignableRoles]);

  // Merge inactive existing role into options if present.
  const allRoles = useMemo(() => {
    if (!existingUserRole) return assignableRoles;
    return [existingUserRole, ...assignableRoles];
  }, [assignableRoles, existingUserRole]);

  // Build SearchableSelect options from the role data.
  const roleOptions: SearchableSelectOption[] = useMemo(() => allRoles.map((r) => ({
    value: r.id,
    label: r.label || r.name,
    description: [
      r.description || null,
      `${r.permissionCount ?? 0} permission${(r.permissionCount ?? 0) !== 1 ? 's' : ''}`,
    ].filter(Boolean).join(' — '),
    searchText: [r.label, r.name, r.description].filter(Boolean).join(' '),
  })), [allRoles]);

  // The selected role object (for the summary card).
  const selectedRole = useMemo(() => allRoles.find((r) => r.id === selectedRoleId) || null, [allRoles, selectedRoleId]);

  // Org-wide roles never need project assignments, mirroring the worker's
  // full-access rule (normalizeRole("Admin") collapses to ORG_ADMIN).
  const fullAccessRole = useMemo(() => {
    if (!selectedRole) return false;
    const canonical = normalizeRole(selectedRole.name || selectedRole.label || '');
    return canonical === ROLES.ORG_ADMIN || canonical === ROLES.SUPER_ADMIN;
  }, [selectedRole]);

  // Restore existing user's role after async load.
  useEffect(() => {
    if (existing) {
      reset({
        name: existing.name,
        uniqueId: existing.unique_id || '',
        email: existing.email || '',
        phone: existing.phone || '',
        roleId: existing.roles?.[0]?.role_id || '',
      });
    }
  }, [existing, reset]);

  // Seed the assignment selection from the user's current assignments so the
  // admin never has to re-add them; removing stays an explicit action.
  useEffect(() => {
    if (isEdit && assignedProjectsQuery.isSuccess) {
      setSelectedProjectIds((assignedProjectsQuery.data ?? []).map((p) => p.id));
    }
  }, [isEdit, assignedProjectsQuery.isSuccess, assignedProjectsQuery.data]);

  const mutation = useMutation({
    mutationFn: (data: any) => {
      const payload: any = {
        name: data.name,
        uniqueId: data.uniqueId,
        phone: data.phone,
        roleIds: [data.roleId],
      };
      if (data.email) payload.email = data.email;
      if (Array.isArray(data.projectIds)) payload.projectIds = data.projectIds;
      if (isEdit) {
        if (data.password) payload.password = data.password;
        return api.put(`/users/${id}`, payload);
      }
      payload.password = data.password;
      return api.post('/users', payload);
    },
    onSuccess: () => { addToast(`User ${isEdit ? 'updated' : 'created'}`, 'success'); navigate('/admin/users'); },
    onError: (err: any) => handleApiSubmitError(err, { addToast, setError: (name, error) => setError(name as any, error) }),
  });

  // Compute the access modules for the selected role summary.
  const accessModules = useMemo(() => {
    if (!selectedRole?.permissions?.length) return [];
    return deriveAccessModules(selectedRole.permissions);
  }, [selectedRole]);

  const visibleModules = accessModules.slice(0, 4);
  const extraCount = accessModules.length - visibleModules.length;

  const showInactiveWarning = existingUserRole && selectedRoleId === existingUserRole.id;

  if (isEdit && loadingExisting) return <PageLoading />;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-heading font-bold text-primary">{isEdit ? 'Edit User' : 'Create User'}</h1>
      <form onSubmit={onSubmitForm} className="space-y-6">
        <Card>
          <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="min-w-0">
              <Label>Name *</Label>
              <Input
                {...register('name')}
                aria-invalid={errors.name ? true : undefined}
                aria-describedby={errors.name ? 'name-error' : undefined}
                className={cn(errors.name && 'border-error')}
              />
              <FormError id="name-error" message={errors.name?.message} />
            </div>
            <div className="min-w-0">
              <Label>Unique ID *</Label>
              <Input
                placeholder="e.g. ENG001"
                {...register('uniqueId')}
                aria-invalid={errors.uniqueId ? true : undefined}
                aria-describedby={errors.uniqueId ? 'uniqueId-error' : undefined}
                className={cn(errors.uniqueId && 'border-error')}
              />
              <FormError id="uniqueId-error" message={errors.uniqueId?.message} />
            </div>
            <div className="min-w-0">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="Optional"
                {...register('email')}
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={errors.email ? 'email-error' : undefined}
                className={cn(errors.email && 'border-error')}
              />
              <FormError id="email-error" message={errors.email?.message} />
            </div>
            <div className="min-w-0">
              <Label>{isEdit ? 'New Password (blank to keep)' : 'Password *'}</Label>
              <PasswordInput
                autoComplete="new-password"
                {...register('password')}
                aria-invalid={errors.password ? true : undefined}
                aria-describedby={errors.password ? 'password-error' : undefined}
                className={cn(errors.password && 'border-error')}
              />
              <FormError id="password-error" message={errors.password?.message} />
            </div>
            <div className="min-w-0">
              <Label>Phone</Label>
              <Input
                {...register('phone')}
                aria-invalid={errors.phone ? true : undefined}
                aria-describedby={errors.phone ? 'phone-error' : undefined}
                className={cn(errors.phone && 'border-error')}
              />
              <FormError id="phone-error" message={errors.phone?.message} />
            </div>
          </CardContent>
        </Card>

        {/* ── Assign Role ──────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Assign Role *</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-secondary">Select the role that determines this user&apos;s access and responsibilities.</p>

            {/* Loading skeleton */}
            {loadingRoles && (
              <div className="flex items-center gap-2 py-2" data-testid="roles-loading">
                <LoadingSpinner size={18} />
                <span className="text-sm text-muted">Loading roles...</span>
              </div>
            )}

            {/* Error state */}
            {rolesError && (
              <div className="flex flex-col gap-2 py-2" data-testid="roles-error">
                <p className="text-sm text-destructive">Unable to load available roles.</p>
                <Button type="button" variant="outline" size="sm" onClick={() => refetchRoles()} data-testid="roles-retry">Retry</Button>
              </div>
            )}

            {/* Searchable dropdown */}
            {!loadingRoles && !rolesError && roleOptions.length > 0 && (
              <div>
                <SearchableSelect
                  value={selectedRoleId}
                  onValueChange={(v) => setValue('roleId', v, { shouldValidate: true })}
                  options={roleOptions}
                  placeholder="Select a role"
                  searchPlaceholder="Search roles..."
                  emptyMessage="No matching roles found."
                  data-field-name="roleId"
                  aria-invalid={!!errors.roleId}
                  aria-describedby={errors.roleId ? 'roleIds-error' : undefined}
                />
              </div>
            )}

            {/* Empty state */}
            {!loadingRoles && !rolesError && roleOptions.length === 0 && (
              <div className="py-2" data-testid="roles-empty">
                <p className="text-sm text-muted">No assignable roles are available. Create and configure a role first.</p>
              </div>
            )}

            {/* Validation error */}
            <div aria-describedby="roleIds-error">
              <FormError id="roleIds-error" message={errors.roleId?.message} />
            </div>

            {/* ── Selected Role Summary Card ──────────────────────────────── */}
            {selectedRole && (
              <div className="rounded-lg border bg-surface-secondary p-4 space-y-3" data-testid="role-summary">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-primary">{selectedRole.label || selectedRole.name}</span>
                      <Badge variant="approved">Selected</Badge>
                      {showInactiveWarning && <Badge variant="rejected">Inactive</Badge>}
                    </div>
                    {selectedRole.description ? (
                      <p className="mt-1 text-sm text-secondary">{selectedRole.description}</p>
                    ) : (
                      <p className="mt-1 text-sm text-muted italic">No description provided</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setValue('roleId', '', { shouldValidate: true })}
                    className="shrink-0 text-xs"
                    data-testid="change-role"
                  >
                    Change role
                  </Button>
                </div>

                {showInactiveWarning && (
                  <p className="text-xs text-destructive">This role is inactive. Select an active role before updating the user.</p>
                )}

                {/* Access modules */}
                {accessModules.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-secondary mb-1">Access:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {visibleModules.map((m) => (
                        <span key={m} className="inline-flex items-center rounded-md bg-surface px-2 py-0.5 text-xs text-secondary border border-default">{m}</span>
                      ))}
                      {extraCount > 0 && (
                        <span className="inline-flex items-center rounded-md bg-surface px-2 py-0.5 text-xs text-muted border border-default">+{extraCount} more</span>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted">{selectedRole.permissionCount ?? 0} permission{(selectedRole.permissionCount ?? 0) !== 1 ? 's' : ''}</span>
                  {selectedRole.permissions && selectedRole.permissions.length > 0 && (
                    <button
                      type="button"
                      className="text-xs text-brand-500 hover:underline"
                      onClick={() => setViewAllPerms((v) => !v)}
                      data-testid="view-permissions"
                    >
                      {viewAllPerms ? 'Hide permissions' : 'View permissions'}
                    </button>
                  )}
                </div>

                {viewAllPerms && selectedRole.permissions && (
                  <div className="flex flex-wrap gap-1" data-testid="permission-list">
                    {selectedRole.permissions.map((p) => (
                      <span key={p.key} className="inline-flex items-center rounded bg-surface px-1.5 py-0.5 text-[11px] text-secondary border border-default" title={p.label}>{p.label}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Project Access ─────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Project Access</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-secondary">Select the projects this user can access.</p>
            {!selectedRole ? (
              <p className="text-sm text-muted">Select a role to configure project access.</p>
            ) : fullAccessRole ? (
              <div className="rounded-lg bg-surface-secondary px-3 py-2.5 text-sm text-primary">
                Full access — {selectedRole.label || selectedRole.name} can see data for every project in the organization.
              </div>
            ) : (
              <>
                <ProjectAccessField
                  options={projectOptions}
                  value={selectedProjectIds}
                  onValueChange={(v) => { setSelectedProjectIds(v); setProjectValidationError(''); }}
                  isLoading={activeProjectsQuery.isLoading}
                  isError={activeProjectsQuery.isError}
                  onRetry={() => activeProjectsQuery.refetch()}
                  emptyMessage="No active projects are available to assign."
                  onProjectCreated={() => activeProjectsQuery.refetch()}
                />
                {!isEdit && <FormError id="projects-error" message={projectValidationError} />}
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col sm:flex-row sm:justify-end gap-3">
          <Button type="button" variant="outline" className="w-full sm:w-auto min-h-[44px]" onClick={() => navigate('/admin/users')}>Cancel</Button>
          <Button type="submit" loading={mutation.isPending} disabled={loadingRoles} className="w-full sm:w-auto min-h-[44px]">{isEdit ? 'Update' : 'Create'}</Button>
        </div>
      </form>
    </div>
  );
}
